import React, { useRef, useEffect } from 'react';
import {
  Box,
  Fab,
  Paper,
  Typography,
  IconButton,
  Chip,
  Slide,
  useMediaQuery,
  useTheme,
  Badge,
  Alert,
  Button,
} from '@mui/material';
import {
  Chat as ChatIcon,
  Close as CloseIcon,
  Minimize as MinimizeIcon,
  SmartToy as BotIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useAIChatContext } from './AIChatContext';
import { useAIChat } from '../../hooks/useAIChat';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';

/**
 * AIChatWidget Component
 *
 * Floating chat widget for AI-powered customer support.
 * Features:
 * - Floating button (bottom-right corner)
 * - Expandable chat window
 * - Full-screen on mobile
 * - Quick action suggestions
 * - Message history with auto-scroll
 */

export const AIChatWidget: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isLargeDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const isExtraLarge = useMediaQuery(theme.breakpoints.up('xl'));

  // Responsive chat widget width
  const chatWidth = isMobile ? '100%' : isExtraLarge ? 480 : isLargeDesktop ? 420 : 380;

  const { isOpen, isMinimized, openChat, closeChat, minimizeChat } = useAIChatContext();
  const {
    messages,
    isLoading,
    error,
    sendMessage,
    retryLastMessage,
    clearError,
    quickActions,
    maxMessageLength,
    isAuthenticated,
  } = useAIChat();

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Check if user is logged in
  const userIsLoggedIn = isAuthenticated();

  // Don't render if user is not authenticated
  if (!userIsLoggedIn) {
    return null;
  }

  const handleQuickAction = (message: string) => {
    sendMessage(message);
  };

  const hasMessages = messages.length > 0;

  return (
    <>
      {/* Floating Chat Button */}
      {!isOpen && (
        <Fab
          color="primary"
          onClick={openChat}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1200,
            boxShadow: 4,
            '&:hover': {
              transform: 'scale(1.05)',
            },
            transition: 'transform 0.2s ease-in-out',
          }}
          aria-label="Open AI Support Chat"
        >
          <Badge
            badgeContent={hasMessages ? messages.filter(m => m.status === 'error').length : 0}
            color="error"
            invisible={!hasMessages || messages.filter(m => m.status === 'error').length === 0}
          >
            <ChatIcon />
          </Badge>
        </Fab>
      )}

      {/* Chat Window */}
      <Slide direction="up" in={isOpen} mountOnEnter unmountOnExit>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: isMobile ? 0 : 24,
            right: isMobile ? 0 : 24,
            width: chatWidth,
            height: isMobile ? '100%' : isMinimized ? 56 : 520,
            maxHeight: isMobile ? '100%' : 'calc(100vh - 48px)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            zIndex: 1300,
            borderRadius: isMobile ? 0 : 3,
            transition: 'height 0.3s ease-in-out',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1.5,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              borderTopLeftRadius: isMobile ? 0 : 12,
              borderTopRightRadius: isMobile ? 0 : 12,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BotIcon />
              <Typography variant="subtitle1" fontWeight={600}>
                AI Support
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {!isMobile && (
                <IconButton
                  size="small"
                  onClick={minimizeChat}
                  sx={{ color: 'inherit' }}
                  aria-label="Minimize chat"
                >
                  <MinimizeIcon fontSize="small" />
                </IconButton>
              )}
              <IconButton
                size="small"
                onClick={closeChat}
                sx={{ color: 'inherit' }}
                aria-label="Close chat"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Chat Content (hidden when minimized) */}
          {!isMinimized && (
            <>
              {/* Error Alert */}
              {error && (
                <Alert
                  severity="error"
                  onClose={clearError}
                  sx={{
                    borderRadius: 0,
                    '& .MuiAlert-message': {
                      fontSize: '0.85rem',
                    },
                  }}
                >
                  {error}
                </Alert>
              )}

              {/* Messages Area */}
              <Box
                ref={messagesContainerRef}
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  px: 2,
                  py: 2,
                  bgcolor: 'background.default',
                }}
              >
                {/* Welcome Message for Empty Chat */}
                {!hasMessages && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      textAlign: 'center',
                      gap: 2,
                      py: 3,
                    }}
                  >
                    <BotIcon
                      sx={{
                        fontSize: 48,
                        color: 'secondary.main',
                        opacity: 0.8,
                      }}
                    />
                    <Typography variant="h6" color="text.primary" gutterBottom>
                      Hi! How can I help?
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ maxWidth: 280, mb: 2 }}
                    >
                      I'm your AI financial assistant. Ask me about your spending, budget, or
                      get help planning a purchase.
                    </Typography>

                    {/* Quick Actions */}
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        width: '100%',
                        maxWidth: 300,
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 600, textTransform: 'uppercase' }}
                      >
                        Quick Questions
                      </Typography>
                      {quickActions.map((action) => (
                        <Chip
                          key={action.message}
                          label={action.label}
                          onClick={() => handleQuickAction(action.message)}
                          variant="outlined"
                          color="primary"
                          sx={{
                            cursor: 'pointer',
                            justifyContent: 'flex-start',
                            height: 'auto',
                            py: 1,
                            '& .MuiChip-label': {
                              whiteSpace: 'normal',
                              textAlign: 'left',
                            },
                            '&:hover': {
                              bgcolor: 'primary.light',
                              color: 'primary.contrastText',
                              borderColor: 'primary.light',
                            },
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Message List */}
                {hasMessages && (
                  <>
                    {messages.map((msg) => (
                      <ChatMessage
                        key={msg.id}
                        message={msg}
                        onRetry={
                          msg.status === 'error' && msg.role === 'user'
                            ? retryLastMessage
                            : undefined
                        }
                      />
                    ))}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </Box>

              {/* Input Area */}
              <ChatInput
                onSend={sendMessage}
                disabled={isLoading}
                maxLength={maxMessageLength}
                placeholder="Ask me anything about your finances..."
              />
            </>
          )}
        </Paper>
      </Slide>
    </>
  );
};

export default AIChatWidget;
